import React from 'react';
import { gql } from '@apollo/client';
import { Typography } from '@mui/material';
import { toSimpleLink } from '../../../lib/helpers/index';
import {
	InstallmentPaymentFailedEventPropsEventFragment,
	UserType,
} from '#types';
import { linkCustomer, LinkCustomerLinkType } from '../helpers';
import { FlattenedUser } from '../../../types/user';
import rpcShared from '@rockpapercoin/rpc-shared';

export const installmentPaymentFailedEventPropsEventFragment = gql`
	fragment installmentPaymentFailedEventPropsEvent on Event {
		id
		invoice {
			id
			title
		}
		contact {
			id
			firstName
			lastName
			customer {
				id
				email
			}
		}
		clientUser {
			id
			user {
				id
			}
			assignedPlanner {
				id
				organization {
					id
				}
			}
		}
	}
`;

export const getInstallmentPaymentFailedEventProps = (
	eventProps: {
		event: InstallmentPaymentFailedEventPropsEventFragment;
		message: React.JSX.Element;
		links: {
			type: string;
			text: string;
			link: any;
			onClick?: ( () => any ) | null;
		}[];
		icon: string;
		className: string;
	},
	user: FlattenedUser
) => {
	const { event } = eventProps;

	if ( !event.invoice || !event.contact ) {
		throw new Error( `Event ID ${ event.id } missing required data` );
	}

	const { invoice, contact } = event;

	const viewerIsAssignedPlannerOrAdmin =
		user?.isLoggedIn && user.userType === UserType.OrgUser && event.clientUser
			? rpcShared.contacts.isAssignedPlannerOrAdminOfCustomersPlannerOrg(
				{ customer: { clientUser: event.clientUser } },
				user
			  )
			: false;

	const viewerIsOrgUser =
		user?.isLoggedIn && user.userType === UserType.OrgUser;

	const invoiceLinkProps = {
		text: invoice.title,
		link: {
			as: `/invoice/${ invoice.id }`,
			href: '/invoice/[id]',
		},
	};

	const defaultEventProps = {
		icon: 'warning',
		links: [
			{
				type: 'secondary',
				text: 'View',
				link: invoiceLinkProps.link,
			},
		],
	};

	if ( viewerIsAssignedPlannerOrAdmin ) {
		if ( !event.clientUser ) {
			throw new Error( `Event ID ${ event.id } missing required data` );
		}
		const invoiceOnBehalfOfLinkProps = {
			text: invoice.title,
			link: {
				as: `/invoice/${ invoice.id }`,
				href: '/invoice/[id]',
			},
		};
		return {
			...defaultEventProps,
			message: (
				<Typography>
					A Payment failed for an invoice belonging to{ ' ' }
					{ linkCustomer( { contact }, LinkCustomerLinkType.Contact ) }:{ ' ' }
					{ toSimpleLink( invoiceOnBehalfOfLinkProps ) }.
				</Typography>
			),
		};
	}

	if ( viewerIsOrgUser ) {
		return {
			...defaultEventProps,
			message: (
				<Typography>
					A Payment failed for an invoice belonging to{ ' ' }
					{ linkCustomer( { contact }, LinkCustomerLinkType.Contact ) }:{ ' ' }
					{ toSimpleLink( invoiceLinkProps ) }.
				</Typography>
			),
		};
	}

	return {
		...defaultEventProps,
		message: (
			<Typography>
				Your payment didn't process, please try that again!
			</Typography>
		),
	};
};
